import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { AlertTriangle, Receipt, Building2, Percent, Briefcase, X, Loader2, CheckCircle2 } from "lucide-react";

/**
 * Client-side helpers + UI for smart flags on entries.
 *
 * The backend at `/api/flags/summary?fy=…` returns aggregate open flags
 * across all non-rejected entries in a FY. This module owns the labels,
 * icons, and inline UI. Flags are advisory — they never block Capture,
 * never block a pack export. They just say "hey, review before send".
 */

// Australian-English labels keyed by backend flag key. Kept in one place
// so the Tax page pre-flight, the EntryEdit panel, and the History row
// badges all read from the same source.
export const FLAG_META = {
  missing_abn_subcontractor: {
    label: "ABN missing",
    fullLabel: "Subcontractor ABN missing",
    icon: Building2,
    tone: "warn",
  },
  no_receipt: {
    label: "No receipt",
    fullLabel: "No receipt attached",
    icon: Receipt,
    tone: "warn",
  },
  gst_mismatch: {
    label: "GST doesn't add up",
    fullLabel: "GST doesn't equal Total ÷ 11",
    icon: Percent,
    tone: "info",
  },
  unassigned_job: {
    label: "No job attached",
    fullLabel: "Entry not linked to a job",
    icon: Briefcase,
    tone: "info",
  },
};

const TONE_CLASSES = {
  warn: "text-[#FBBF24] bg-[#FBBF24]/12 border-[#FBBF24]/25",
  info: "text-[#60A5FA] bg-[#60A5FA]/10 border-[#60A5FA]/20",
};

/**
 * <FlagPill /> — small inline chip. Used in History rows to hint that
 * an entry has at least one open flag; taps just show the count so the
 * user can drill down inside EntryEdit.
 */
export function FlagPill({ count, testid }) {
  if (!count) return null;
  return (
    <span
      data-testid={testid || "flag-pill"}
      className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[9.5px] font-heading font-bold uppercase tracking-wider text-[#FBBF24] bg-[#FBBF24]/12 border border-[#FBBF24]/25"
      title={`${count} open flag${count === 1 ? "" : "s"} — open the entry to review`}
    >
      <AlertTriangle className="w-2.5 h-2.5" />
      {count}
    </span>
  );
}

/**
 * <EntryFlags entryId /> — full flag panel with dismiss buttons. Used
 * on EntryEdit and Review pages. Fetches its own data on mount (cheap;
 * single doc lookup + supplier-twin join for one entry).
 *
 * We ride the summary endpoint (rather than a per-entry endpoint) to
 * keep the surface area small and reuse the same computation logic.
 * Filter client-side to entryId.
 */
export function EntryFlags({ entryId, onChanged, className = "" }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState({});

  const load = useCallback(async () => {
    if (!entryId) return;
    setLoading(true);
    try {
      // No FY filter — surface flags regardless of when the entry was
      // logged. Tradies fix things when they see them, not when the FY
      // filter agrees.
      const { data } = await api.get("/flags/summary");
      const mine = (data?.flags || []).filter((f) => f.entry_id === entryId);
      setFlags(mine);
    } catch (err) {
      devLog.warn("flags fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [entryId]);

  useEffect(() => { load(); }, [load]);

  const dismiss = useCallback(async (key) => {
    setDismissing((d) => ({ ...d, [key]: true }));
    try {
      await api.post(`/entries/${entryId}/dismiss-flag`, { key });
      // Optimistic UI — drop the flag instantly instead of waiting for the
      // next summary fetch.
      setFlags((f) => f.filter((x) => x.key !== key));
      onChanged?.();
    } catch (err) {
      devLog.warn("flag dismiss failed", err);
    } finally {
      setDismissing((d) => ({ ...d, [key]: false }));
    }
  }, [entryId, onChanged]);

  if (loading) {
    return (
      <div className={`text-[11px] text-white/40 inline-flex items-center gap-1.5 ${className}`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Checking flags…
      </div>
    );
  }
  if (!flags.length) {
    return (
      <div className={`inline-flex items-center gap-1.5 text-[11px] text-[#34D399] ${className}`} data-testid="entry-flags-clean">
        <CheckCircle2 className="w-3.5 h-3.5" /> No open flags on this entry.
      </div>
    );
  }
  return (
    <div className={`space-y-1.5 ${className}`} data-testid="entry-flags">
      {flags.map((f) => {
        const meta = FLAG_META[f.key] || { label: f.key, tone: "warn", icon: AlertTriangle };
        const Icon = meta.icon;
        return (
          <div
            key={f.key}
            data-testid={`entry-flag-${f.key}`}
            className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${TONE_CLASSES[meta.tone]}`}
          >
            <Icon className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-heading font-bold uppercase tracking-wider">{meta.fullLabel}</div>
              <div className="text-[11px] text-white/80 mt-0.5">{f.hint}</div>
            </div>
            <button
              type="button"
              onClick={() => dismiss(f.key)}
              disabled={!!dismissing[f.key]}
              data-testid={`entry-flag-dismiss-${f.key}`}
              className="shrink-0 -mr-1 p-1 rounded hover:bg-white/10 disabled:opacity-50"
              aria-label={`Dismiss ${meta.fullLabel}`}
              title="Mark as reviewed"
            >
              {dismissing[f.key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
            </button>
          </div>
        );
      })}
    </div>
  );
}
